import { useEffect, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { eventService } from '../../services/eventService';

interface DocProcessingState {
  filename: string;
  status: string;
  progress: number;
  batch: string;
  projectId: string;
  creditsDeducted?: number;
  pageCount?: number;
  batchComplete?: boolean;
}

interface DocProcessingEvent {
  status?: string;
  filename?: string;
  progress?: number;
  batch?: string;
  project_id?: string;
  credits_deducted?: number;
  page_count?: number;
  batch_complete?: boolean;
}

type DismissTimerRef = MutableRefObject<ReturnType<typeof setTimeout> | null>;
type DocProcessingSetter = Dispatch<SetStateAction<DocProcessingState | null>>;

const isTerminalEvent = (event: DocProcessingEvent): boolean => (
  event.status === 'completed' || event.status === 'complete' || event.status === 'error'
);

const cancelDismiss = (timerRef: DismissTimerRef): void => {
  if (!timerRef.current) return;
  clearTimeout(timerRef.current);
  timerRef.current = null;
};

const terminalState = (
  previous: DocProcessingState | null,
  event: DocProcessingEvent,
): DocProcessingState | null => previous && ({
  ...previous,
  progress: 100,
  status: event.status ?? 'complete',
  creditsDeducted: event.credits_deducted,
  pageCount: event.page_count,
  batchComplete: event.batch_complete,
});

const activeState = (event: DocProcessingEvent): DocProcessingState => ({
  filename: event.filename ?? '',
  status: event.status ?? 'processing',
  progress: event.progress ?? 0,
  batch: event.batch ?? '',
  projectId: event.project_id ?? '',
  batchComplete: event.batch_complete,
});

const applyTerminalEvent = (
  event: DocProcessingEvent,
  setState: DocProcessingSetter,
  timerRef: DismissTimerRef,
): void => {
  setState((previous) => terminalState(previous, event));
  if (event.batch_complete === false) return;
  timerRef.current = setTimeout(() => {
    setState(null);
    timerRef.current = null;
  }, 3500);
};

const applyDocProcessingEvent = (
  data: unknown,
  setState: DocProcessingSetter,
  timerRef: DismissTimerRef,
): void => {
  const event = data as DocProcessingEvent;

  cancelDismiss(timerRef);
  if (isTerminalEvent(event)) {
    applyTerminalEvent(event, setState, timerRef);

    return;
  }
  setState(activeState(event));
};

/**
 * Subscribes to `doc_processing` SSE events and exposes the current
 * document processing state.  Auto-clears after the progress reaches
 * 100 % (or the `complete` / `error` status is received).
 *
 * Agent suggestion buttons are handled by the worker, which runs a
 * supervisor check after processing and broadcasts `suggested_agents`
 * via SSE.  The eventService bridges that to window.CustomEvent.
 */
export const useDocProcessing = (): DocProcessingState | null => {
  const [state, setState] = useState<DocProcessingState | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const unsubscribe = eventService.subscribe(
      'doc_processing',
      (data) => applyDocProcessingEvent(data, setState, dismissTimerRef),
    );

    return () => {
      cancelDismiss(dismissTimerRef);
      unsubscribe();
    };
  }, []);

  return state;
};


// ============================================================================
// useDocUploadChoice — shows the Replace/Merge/Supplement prompt when a
// second document is uploaded to a project that already has an SRD.
// ============================================================================

export interface DocUploadChoiceState {
  filename: string;
  jobKey: string;
  projectId: string;
  recommended: 'replace' | 'merge';
}

/**
 * Subscribes to `doc_upload_choice` SSE events.
 * Returns the pending choice state (cleared once the user picks an option).
 */
export const useDocUploadChoice = (): [
  DocUploadChoiceState | null,
  () => void,
] => {
  const [choice, setChoice] = useState<DocUploadChoiceState | null>(null);

  const dismiss = () => setChoice(null);

  useEffect(() => {
    const unsub = eventService.subscribe('doc_upload_choice', (data) => {
      const d = data as {
        filename?: string;
        job_key?: string;
        project_id?: string;
        recommended?: string;
      };

      if (d.filename && d.job_key && d.project_id) {
        setChoice({
          filename: d.filename,
          jobKey: d.job_key,
          projectId: d.project_id,
          recommended: (d.recommended === 'merge' ? 'merge' : 'replace') as 'replace' | 'merge',
        });
      }
    });

    return unsub;
  }, []);

  return [choice, dismiss];
};
